import React, { useState, useMemo, useEffect, useRef, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase'; // make sure firebase.js exports `storage`
import { AppContext } from '../AppContext';
import { getFirestore, doc, onSnapshot, runTransaction } from 'firebase/firestore';

const ReleaseProduct = () => {
  const navigate = useNavigate();
  const { isLoggedIn } = useContext(AppContext);
  const db = getFirestore();

  const [activeTab, setActiveTab] = useState('release'); // 'release' | 'orders'

  // ---------- Release Product state ----------
  const [name, setName] = useState('');
  const [priceUsd, setPriceUsd] = useState('');
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState([]); // File[]
  const [submitting, setSubmitting] = useState(false);
  const [progressMap, setProgressMap] = useState({}); // index -> %
  const fileInputRef = useRef(null);

  // ---------- Orders state (View Orders tab) ----------
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [fulfillBusyId, setFulfillBusyId] = useState(null);

  useEffect(() => {
    if (isLoggedIn === false) navigate('/login');
  }, [isLoggedIn, navigate]);

  // Attach/Detach listener for orders when tab is "orders"
  useEffect(() => {
    let unsub = null;
    if (activeTab === 'orders') {
      setOrdersLoading(true);
      const ref = doc(db, 'streetFlavorOrders', 'merchOrders');
      unsub = onSnapshot(
        ref,
        (snap) => {
          const data = snap.data() || {};
          const arr = Array.isArray(data.orders) ? data.orders : [];
          // filter out fulfilled
          const filtered = arr.filter(o => !o?.orderFulfilled);
          // sort newest first by createdAt or chargedCents fallback
          filtered.sort((a, b) => {
            const ta = a?.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
            const tb = b?.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
            return tb - ta;
          });
          setOrders(filtered);
          setOrdersLoading(false);
        },
        (err) => {
          console.error('[Orders listener] error', err);
          setOrders([]);
          setOrdersLoading(false);
        }
      );
    }
    return () => {
      if (unsub) unsub();
    };
  }, [activeTab, db]);

  const previews = useMemo(() => files.map(f => URL.createObjectURL(f)), [files]);
  useEffect(() => () => previews.forEach(url => URL.revokeObjectURL(url)), [previews]);

  const onPickClick = () => fileInputRef.current?.click();
  const onPickFiles = (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;
    setFiles(prev => [...prev, ...list]);
    e.target.value = '';
  };
  const removeFile = (idx) => setFiles(prev => prev.filter((_, i) => i !== idx));

  const priceCents = useMemo(() => {
    const n = Number(priceUsd);
    if (!Number.isFinite(n) || n <= 0) return 0;
    return Math.round(n * 100);
  }, [priceUsd]);

  const validate = () => {
    if (!name.trim()) return 'Please enter a product name.';
    if (!description.trim()) return 'Please enter a product description.';
    if (priceCents <= 0) return 'Please enter a valid price greater than 0.';
    if (files.length < 1) return 'Please select at least one image.';
    return '';
  };

  const uploadOne = (file, path, idx) =>
    new Promise((resolve, reject) => {
      const sRef = storageRef(storage, path);
      const task = uploadBytesResumable(sRef, file);
      task.on(
        'state_changed',
        (snap) => {
          const pct = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
          setProgressMap(m => ({ ...m, [idx]: pct }));
        },
        (err) => reject(err),
        async () => {
          try {
            const url = await getDownloadURL(task.snapshot.ref);
            resolve(url);
          } catch (e) {
            reject(e);
          }
        }
      );
    });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { alert(err); return; }
    setSubmitting(true);
    try {
      const ts = Date.now();
      const urls = await Promise.all(
        files.map((f, i) => uploadOne(f, `streetflavor_products/${ts}_${i}_${f.name}`, i))
      );

      const fn = httpsCallable(getFunctions(), 'releaseBizarreProduct');
      const res = await fn({
        name: name.trim(),
        description: description.trim(),
        priceCents,
        images: urls,
      });
      if (!res?.data?.ok) throw new Error('Cloud function did not return ok');

      // success → reset and go to shop
      setName(''); setPriceUsd(''); setDescription('');
      setFiles([]); setProgressMap({});
      navigate('/shop');
    } catch (e2) {
      console.error('Release error:', e2);
      alert(e2?.message || 'Unable to release product. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const markFulfilled = async (order) => {
    const ok = window.confirm('Have you shipped this item? Marking it as fulfilled will remove it from this orders page');
    if (!ok) return;
    try {
      setFulfillBusyId(order.paymentIntentId || 'x');
      const ref = doc(db, 'streetFlavorOrders', 'merchOrders');
      await runTransaction(db, async (tx) => {
        const snap = await tx.get(ref);
        const data = snap.data() || {};
        const arr = Array.isArray(data.orders) ? data.orders.slice() : [];
        const idx = arr.findIndex(o => (o?.paymentIntentId || '') === (order?.paymentIntentId || ''));
        if (idx === -1) throw new Error('Order not found in document.');
        arr[idx] = { ...arr[idx], orderFulfilled: true };
        tx.set(ref, { orders: arr }, { merge: true });
      });
if (order?.uid) {
  const userOrdersRef = doc(db, 'users', order.uid, 'streetFlavorOrders', 'merchOrders');
  await runTransaction(db, async (tx2) => {
    const snap2 = await tx2.get(userOrdersRef);
    const data2 = snap2.data() || {};
    const arr2 = Array.isArray(data2.orders) ? data2.orders.slice() : [];
    const idx2 = arr2.findIndex(o =>
      (o?.paymentIntentId || '') === (order?.paymentIntentId || '')
    );
    if (idx2 !== -1) {
      arr2[idx2] = {
        ...arr2[idx2],
        orderFulfilled: true,
        orderStatus: 'shipped',   // <-- this drives "Shipped" in MyOrders
      };
      tx2.set(userOrdersRef, { orders: arr2 }, { merge: true });
    }
  });
}

    } catch (err) {
      console.error('Fulfill update failed:', err);
      alert(err?.message || 'Could not mark as fulfilled.');
    } finally {
      setFulfillBusyId(null);
    }
  };

  const fmtUSD = (cents) =>
    (Number(cents || 0) / 100).toLocaleString(undefined, { style: 'currency', currency: 'USD' });

  return (
    <div className="sf-release-page">
      <div className="sf-tabs">
        <button
          className={`sf-tab ${activeTab === 'release' ? 'active' : ''}`}
          onClick={() => setActiveTab('release')}
        >
          Release Product
        </button>
        <button
          className={`sf-tab ${activeTab === 'orders' ? 'active' : ''}`}
          onClick={() => setActiveTab('orders')}
        >
          View Orders
        </button>
      </div>

      {activeTab === 'release' ? (
        <>
          <h1 className="sf-title">Release Product</h1>

          <form className="sf-form" onSubmit={handleSubmit}>
            <label className="sf-label">
              Product Name
              <input
                className="sf-input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Street Flavor Tee"
                disabled={submitting}
              />
            </label>

            <label className="sf-label">
              Price (USD)
              <input
                className="sf-input"
                type="number"
                step="0.01"
                min="0"
                value={priceUsd}
                onChange={(e) => setPriceUsd(e.target.value)}
                placeholder="29.99"
                disabled={submitting}
              />
            </label>

            <label className="sf-label">
              Description
              <textarea
                className="sf-textarea"
                rows={5}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Details about the product..."
                disabled={submitting}
              />
            </label>

            <div className="sf-files">
              <div className="sf-file-row">
                <button
                  type="button"
                  className="sf-btn upload"
                  onClick={onPickClick}
                  disabled={submitting}
                  title="Choose one or more images"
                >
                  + Choose Images
                </button>
                <span className="sf-file-hint">
                  {files.length ? `${files.length} image${files.length > 1 ? 's' : ''} selected` : 'At least one image required'}
                </span>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={onPickFiles}
                  className="sf-file-hidden"
                  aria-hidden="true"
                  tabIndex={-1}
                />
              </div>

              {!!files.length && (
                <div className="sf-thumbs">
                  {files.map((f, idx) => (
                    <div key={idx} className="sf-thumb">
                      <img src={previews[idx]} alt={f.name} />
                      <div className="sf-thumb-meta">
                        <div className="sf-thumb-name" title={f.name}>{f.name}</div>
                        {submitting && (
                          <div className="sf-progress">
                            <div className="sf-progress-bar" style={{ width: `${progressMap[idx] || 0}%` }} />
                          </div>
                        )}
                      </div>
                      {!submitting && (
                        <button type="button" className="sf-remove" onClick={() => removeFile(idx)} title="Remove">
                          ✕
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="sf-actions">
              <button type="submit" className="sf-btn release" disabled={submitting}>
                {submitting ? 'Releasing…' : 'RELEASE PRODUCT'}
              </button>
              <button type="button" className="sf-btn ghost" onClick={() => navigate('/shop')} disabled={submitting}>
                Cancel
              </button>
            </div>
          </form>
        </>
      ) : (
        <>
          <h1 className="sf-title">Orders</h1>
          <div className="sf-form">
            {ordersLoading ? (
              <p>Loading orders…</p>
            ) : !orders.length ? (
              <p>No unfulfilled orders right now.</p>
            ) : (
              <ul className="sf-orders">
                {orders.map((o) => {
                  const created = o?.createdAt?.toDate ? o.createdAt.toDate() : null;
                  return (
                    <li key={o.paymentIntentId} className="sf-order">
                      <div className="sf-order-head">
                        <div>
                          <div className="sf-order-id">PI: {o.paymentIntentId}</div>
                          <div className="sf-order-date">
                            {created ? created.toLocaleString() : '—'}
                          </div>
                        </div>
                        <button
                          className="sf-btn"
                          onClick={() => markFulfilled(o)}
                          disabled={fulfillBusyId === o.paymentIntentId}
                        >
                          {fulfillBusyId === o.paymentIntentId ? 'Marking…' : 'Mark Fulfilled'}
                        </button>
                      </div>

                      <div className="sf-order-grid">
                        <div className="sf-order-box">
                          <div className="sf-order-title">Items</div>
                          <ul className="sf-order-items">
                            {(o.items || []).map((it, idx) => (
                              <li key={idx} className="sf-order-item">
                                <div className="left">
                                  <div className="name">{it.name || it.id}</div>
                                  <div className="meta">Qty {it.qty}</div>
                                </div>
                                <div className="right">{fmtUSD(it.unitCents)}</div>
                              </li>
                            ))}
                          </ul>
                        </div>

                        <div className="sf-order-box">
                          <div className="sf-order-title">Totals</div>
                          <div className="sf-order-totals">
                            <div><span>Subtotal</span><span>{fmtUSD(o?.totals?.subtotalCents)}</span></div>
                            <div><span>Shipping</span><span>{fmtUSD(o?.totals?.shippingCents)}</span></div>
                            <div><span>Tax</span><span>{fmtUSD(o?.totals?.taxCents)}</span></div>
                            {o?.totals?.walletAppliedCents > 0 && (
                              <div><span>Wallet Applied</span><span>-{fmtUSD(o?.totals?.walletAppliedCents)}</span></div>
                            )}
                            <div className="sf-order-total">
                              <span>Charged</span>
                              <span>{fmtUSD(o?.totals?.chargedCents)}</span>
                            </div>
                          </div>
                        </div>

                        <div className="sf-order-box">
                          <div className="sf-order-title">Ship To</div>
                          <div className="sf-order-ship">
                            <div className="name">{o?.shipping?.name}</div>
                            <div>{o?.shipping?.address?.line1}</div>
                            {o?.shipping?.address?.line2 ? <div>{o.shipping.address.line2}</div> : null}
                            <div>
                              {o?.shipping?.address?.city}{o?.shipping?.address?.city ? ', ' : ''}
                              {o?.shipping?.address?.state} {o?.shipping?.address?.postal_code}
                            </div>
                            <div>{o?.shipping?.address?.country}</div>
                          </div>
                        </div>

                        <div className="sf-order-box">
                          <div className="sf-order-title">Contact</div>
                          <div className="sf-order-contact">
                            <div>{o?.contact?.name}</div>
                            <div>{o?.contact?.email}</div>
                            <div>{o?.contact?.phone}</div>
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </>
      )}

      <style>{`
:root{
  --sf-gold:#fdca05;
  --sf-gold-10:rgba(128,128,78,.10);
  --sf-gold-18:rgba(128,128,78,.18);
  --sf-panel:#0b0b0b;
  --sf-bd:rgba(255,255,255,.10);
  --sf-bd-2:rgba(255,255,255,.18);
}
body {
  font-family: 'CustomFont', sans-serif;
  margin: 0;
  padding: 0;
  background-color: #000;
  color: #fff;
}
.sf-release-page{
  background:#000;
  min-height:calc(100vh - 120px);
  padding:20px 16px 80px;
  color:#fff;
  max-width:1024px;
  margin:0 auto;
}

/* Tabs */
.sf-tabs{ display:flex; gap:10px; margin-bottom:12px; }
.sf-tab{
  padding:.6rem 1rem;
  border-radius:10px;
  border:1px solid var(--sf-bd-2);
  background:#111;
  color:#fff;
  cursor:pointer;
  font-weight:900;
}
.sf-tab.active{
  border-color:var(--sf-gold);
  background:var(--sf-gold);
  color:#000;
  box-shadow:0 6px 18px rgba(128,128,78,.26);
}

.sf-title{
  font-size:2.0rem;
  font-weight:900;
  color:#fff;
  letter-spacing:.02em;
  text-shadow:0 0 18px var(--sf-gold-10);
  margin:4px 0 14px;
}

.sf-form{
  background:var(--sf-panel);
  border:1px solid var(--sf-bd);
  border-radius:14px;
  padding:18px;
  display:flex;
  flex-direction:column;
  gap:16px;
  box-shadow:0 6px 24px rgba(0,0,0,.35);
}

.sf-label{
  display:flex; flex-direction:column; gap:8px;
  font-weight:800; color:#fff;
}

.sf-input, .sf-textarea{
  width:100%;
  background:rgba(255,255,255,.06);
  border:1px solid var(--sf-bd-2);
  color:#fff;
  border-radius:12px;
  padding:12px 14px;
  font-size:1rem;
  outline:none;
}
.sf-input::placeholder, .sf-textarea::placeholder{ color:rgba(255,255,255,.55); }
.sf-input:focus, .sf-textarea:focus{
  border-color:var(--sf-gold);
  box-shadow:0 0 0 2px var(--sf-gold-10) inset;
}

.sf-files{}
.sf-file-row{ display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
.sf-file-hint{ opacity:.8; font-size:.95rem; }
.sf-file-hidden{ display:none; }

.sf-thumbs{
  display:grid;
  grid-template-columns:repeat(auto-fill,minmax(170px,1fr));
  gap:12px;
  margin-top:12px;
}
.sf-thumb{
  position:relative;
  background:#111;
  border:1px solid var(--sf-gold-18);
  border-radius:12px;
  overflow:hidden;
}
.sf-thumb img{ width:100%; height:150px; object-fit:cover; display:block; }
.sf-thumb-meta{ padding:8px; }
.sf-thumb-name{ font-size:.88rem; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; opacity:.95; }
.sf-remove{
  position:absolute; top:6px; right:6px;
  background:rgba(0,0,0,.65);
  border:1px solid var(--sf-gold-18);
  color:#fff; border-radius:10px; padding:2px 8px; cursor:pointer;
}

.sf-progress{ margin-top:6px; height:6px; background:rgba(255,255,255,.15); border-radius:999px; overflow:hidden; }
.sf-progress-bar{ height:6px; background:var(--sf-gold); }

.sf-actions{ display:flex; gap:12px; flex-wrap:wrap; margin-top:4px; }

.sf-btn{
  padding:.68rem 1.15rem;
  border-radius:12px;
  font-weight:900;
  cursor:pointer;
  border:1px solid var(--sf-gold);
  background:var(--sf-gold);
  color:#000;
  box-shadow:0 6px 18px rgba(128,128,78,.26);
  transition:transform .15s ease, box-shadow .15s ease, opacity .15s ease;
}
.sf-btn:hover{ transform:translateY(-1px); box-shadow:0 10px 22px rgba(128,128,78,.32); }

.sf-btn.release{ padding:.9rem 1.4rem; letter-spacing:.04em; font-size:1.08rem; border-width:2px; }
.sf-btn.ghost{
  background:transparent; color:var(--sf-gold);
  border:1px solid var(--sf-gold-18); box-shadow:none;
}
.sf-btn.upload{
  background:var(--sf-gold); color:#000;
  border:1px solid var(--sf-gold); font-weight:900;
}

@media (max-width:640px){ .sf-title{ font-size:1.7rem; } }

/* Box model hardening */
*, *::before, *::after { box-sizing: border-box; }
.sf-form { width: 100%; overflow: hidden; }
.sf-label { width: 100%; }
.sf-input, .sf-textarea { box-sizing: border-box; width: 100%; max-width: 100%; display: block; margin: 0; }
.sf-input[type="number"] { -webkit-appearance: none; appearance: textfield; }
.sf-input[type="number"]::-webkit-outer-spin-button,
.sf-input[type="number"]::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }

/* Orders list */
.sf-orders{ list-style:none; padding:0; margin:0; display:flex; flex-direction:column; gap:14px; }
.sf-order{
  border:1px solid var(--sf-bd-2);
  border-radius:12px;
  padding:12px;
  background:#0b0b0b;
}
.sf-order-head{
  display:flex; justify-content:space-between; align-items:center; gap:12px; margin-bottom:10px;
}
.sf-order-id{ font-weight:900; }
.sf-order-date{ opacity:.85; font-size:.94rem; }

.sf-order-grid{
  display:grid; gap:10px;
  grid-template-columns: 2fr 1.2fr 1.2fr 1fr;
}
@media (max-width: 920px){
  .sf-order-grid{ grid-template-columns: 1fr; }
}

.sf-order-box{
  border:1px solid var(--sf-bd);
  border-radius:10px;
  padding:10px;
  background:#0f0f0f;
}
.sf-order-title{ font-weight:900; margin-bottom:8px; }

.sf-order-items{ list-style:none; padding:0; margin:0; display:flex; flex-direction:column; gap:8px; }
.sf-order-item{ display:flex; justify-content:space-between; align-items:center; gap:10px; }
.sf-order-item .name{ font-weight:800; }
.sf-order-item .meta{ opacity:.85; font-size:.9rem; }

.sf-order-totals{ display:flex; flex-direction:column; gap:6px; }
.sf-order-totals > div{ display:flex; justify-content:space-between; }
.sf-order-total{ font-weight:900; color:var(--sf-gold); }

.sf-order-ship, .sf-order-contact{ display:flex; flex-direction:column; gap:4px; }
.sf-order-ship .name{ font-weight:800; }
      `}</style>

    </div>
  );
};

export default ReleaseProduct;
